import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { useBooking } from './BookingContext';
export function HeroSection() {
  const {
    openBooking
  } = useBooking();
  const parallaxRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Only run parallax on larger, non-touch screens. On mobile the scroll-driven
    // transform fights the browser's address-bar resize and reads as jitter, so we
    // simply leave the background static there.
    const canParallax = window.matchMedia('(min-width: 768px) and (pointer: fine)').matches;
    if (!canParallax) return;
    let raf = 0;
    const handleScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        if (parallaxRef.current) {
          const scrolled = window.scrollY;
          parallaxRef.current.style.transform = `translate3d(0, ${scrolled * 0.5}px, 0)`;
        }
      });
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);
  const scrollToRSVP = () => {
    const element = document.getElementById('rsvp');
    if (element) {
      const offset = 72;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-bc-bg" data-id="element-82">
      {/* Parallax background layers */}
      <div ref={parallaxRef} className="absolute inset-0 -top-[10%] h-[120%] bc-vignette bc-grain" data-id="element-83">
        {/* Real Be CLINICAL interior, treated as an after-hours room */}
        <img src="https://cdn.magicpatterns.com/uploads/phoiThz1tWcunwXKQzDDLo/Be_Clinical_Event.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover brightness-[0.58] contrast-[1.04] saturate-[0.95]" data-id="element-84" />
        {/* Wash to keep headline legible while the room stays visible */}
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(7,7,12,0.45) 0%, rgba(7,7,12,0.3) 45%, rgba(7,7,12,0.78) 100%)'
      }} data-id="element-85" />
        {/* Pink neon rim-light glow */}
        <div ref={glowRef} className="absolute inset-0" style={{
        background: 'radial-gradient(120% 90% at 70% 40%, rgba(255, 31, 143, 0.28), transparent 55%)',
        animation: 'bc-flicker 1.2s ease-out'
      }} data-id="element-86" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-32 text-center" data-id="element-87">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-88">
          {/* Logo mark */}
          <img src="https://cdn.magicpatterns.com/uploads/pVcT9SJxmKezCoeaC5imfz/Be_Clinical_logo_white_hex.png" alt="Be CLINICAL" className="mx-auto h-12 md:h-14 w-auto mb-2" data-id="element-89" />

          {/* Eyebrow */}
          <div className="flex items-center justify-center gap-2 text-bc-pink font-sans text-sm md:text-base tracking-wide2 uppercase" data-id="element-90">
            <span className="text-base" data-id="element-91">✦</span>
            <span data-id="element-92">AN EXCLUSIVE EVENING — BY INVITATION</span>
          </div>

          {/* Headline */}
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-bc-bone bc-glow-text leading-tight" data-id="element-93">
            THE COLLAGEN RESET EVENT
          </h1>

          {/* Subhead */}
          <p className="font-sans font-medium text-lg md:text-xl tracking-wide2 uppercase text-bc-bone" data-id="element-94">
            THE FUTURE OF SKIN REGENERATION
          </p>

          {/* Big bold date & location */}
          <div className="pt-4 pb-2 space-y-2" data-id="element-95">
            <p className="font-display font-extrabold text-3xl sm:text-4xl md:text-5xl text-bc-bone bc-glow-text leading-none" data-id="element-96">
              WEDNESDAY, JUNE 24
            </p>
            <p className="font-sans font-bold text-xl sm:text-2xl md:text-3xl tracking-wide2 uppercase text-bc-pink-soft" data-id="element-97">
              6 – 8 PM &nbsp;·&nbsp; SAN RAMON, CA
            </p>
          </div>

          {/* Accent line */}
          <p className="font-serif italic text-xl md:text-2xl text-bc-pink-soft pt-2" data-id="element-98">
            Designed to help skin behave younger.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8" data-id="element-99">
            <Button onClick={openBooking} className="rounded-full bg-transparent border border-bc-pink text-bc-bone text-base tracking-label uppercase bc-glow-box hover:bc-glow-box-soft transition-all bc-ease px-8 h-14 w-full sm:w-auto" data-id="element-100">
              RESERVE YOUR SPOT
              <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} data-id="element-101" />
            </Button>
            <a href="tel:925-215-1160" className="text-bc-bone-2 font-sans text-base hover:text-bc-bone transition-colors bc-ease" data-id="element-102">
              OR CALL 925-215-1160
            </a>
          </div>
        </div>
      </div>
    </section>;
}